/* global IDBDatabase, indexedDB */
/**
 * Library card pictures kept in this browser, so a page that opens again does
 * not read every card's picture from the server again (a Firestore read each).
 *
 * Keyed by owner, asset AND picture version: a newer picture is a new key, so
 * nothing here can go stale. IndexedDB, because a few hundred 15 kB pictures
 * would fill localStorage. Every call is wrapped: a private window or blocked
 * storage simply misses, and the picture is read from the server.
 */
const DB = 'dw-asset-thumbs';
const STORE = 'thumbs';

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      // No IndexedDB here (a private window, jsdom): every lookup misses.
      resolve(null);
    }
  });
}

let db: Promise<IDBDatabase | null> | null = null;
const handle = () => (db ??= open());

export async function cachedThumb(key: string): Promise<string | null> {
  const store = await handle();
  if (!store) return null;
  return new Promise((resolve) => {
    const req = store.transaction(STORE).objectStore(STORE).get(key);
    req.onsuccess = () => resolve(typeof req.result === 'string' ? req.result : null);
    req.onerror = () => resolve(null);
  });
}

export async function cacheThumb(key: string, data: string): Promise<void> {
  const store = await handle();
  if (!store) return;
  await new Promise<void>((resolve) => {
    const tx = store.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(data, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}
